'use client'

import { useState, useMemo } from 'react'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { ChevronDown, ChevronRight, FolderOpen, Tag } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLocale } from '@/lib/i18n'
import { useActiveCategoryOverride } from '@/lib/blog/active-category-context'
import type { CategoryWithCountNode, TagWithTranslation } from '@/lib/blog/article-types'

interface CategorySidebarProps {
  categories: CategoryWithCountNode[]
  tags: TagWithTranslation[]
  locale: string
}

function useActiveCategorySlug(): string | null {
  const pathname = usePathname()
  const { overrideSlug } = useActiveCategoryOverride()
  return useMemo(() => {
    const match = pathname.match(/\/blog\/[^/]+\/category\/([^/]+)/)
    if (match) return match[1]
    return overrideSlug
  }, [pathname, overrideSlug])
}

function useActiveTags(): string[] {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  return useMemo(() => {
    const tagsParam = searchParams.get('tags')
    if (tagsParam) return tagsParam.split(',').filter(Boolean)
    // fall back to path-based single tag for backward compat
    const match = pathname.match(/\/blog\/[^/]+\/tag\/([^/]+)/)
    return match ? [match[1]] : []
  }, [searchParams, pathname])
}

function CategoryItem({
  category,
  locale,
  activeCategorySlug,
  openCategoryId,
  onToggle,
  level = 0,
}: {
  category: CategoryWithCountNode
  locale: string
  activeCategorySlug?: string
  openCategoryId: string | null
  onToggle: (id: string) => void
  level?: number
}) {
  const hasChildren = category.children && category.children.length > 0
  const isActive = category.slug === activeCategorySlug
  const isOpen = openCategoryId === category.id

  return (
    <div className="space-y-0.5">
      <div
        className={cn(
          'group flex items-center gap-2 rounded-xl transition-all duration-200',
          isActive
            ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md translate-x-1'
            : 'text-foreground/90 hover:translate-x-1 hover:bg-accent/70 hover:text-foreground'
        )}
      >
        {hasChildren && (
          <button
            onClick={() => onToggle(category.id)}
            className={cn(
              'p-1.5 rounded-md transition-colors',
              isActive
                ? 'hover:bg-white/20 text-white'
                : 'text-muted-foreground hover:bg-background/70 hover:text-foreground'
            )}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
          >
            {isOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        )}

        <Link
          href={`/blog/${locale}/category/${category.slug}`}
          className={cn(
            'flex-1 flex items-center justify-between py-3 pr-3',
            !hasChildren && 'pl-4',
            level > 0 && 'pl-3'
          )}
        >
          <span
            className={cn(
              'text-sm font-medium truncate',
              isActive ? 'text-white' : 'text-foreground/90 group-hover:text-foreground'
            )}
          >
            {category.translatedName}
          </span>
          {category.articleCount > 0 && (
            <span
              className={cn(
                'ml-2 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
                isActive
                  ? 'bg-white/20 text-white'
                  : 'border border-border/70 bg-background/70 text-muted-foreground'
              )}
            >
              {category.articleCount}
            </span>
          )}
        </Link>
      </div>

      {hasChildren && isOpen && (
        <div className={cn('ml-4 space-y-0.5 border-l border-border/70 pl-2')}>
          {category.children.map((child) => (
            <CategoryItem
              key={child.id}
              category={child}
              locale={locale}
              activeCategorySlug={activeCategorySlug}
              openCategoryId={openCategoryId}
              onToggle={onToggle}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function CategorySidebar({
  categories,
  tags,
  locale,
}: CategorySidebarProps) {
  const activeCategorySlug = useActiveCategorySlug()
  const activeTags = useActiveTags()
  const { t } = useLocale()
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null)

  function handleToggle(id: string) {
    setOpenCategoryId(prev => prev === id ? null : id)
  }

  function getTagHref(slug: string): string {
    const next = activeTags.includes(slug)
      ? activeTags.filter(s => s !== slug)
      : [...activeTags, slug]
    if (next.length === 0) return `/blog/${locale}`
    return `/blog/${locale}?tags=${next.join(',')}`
  }

  return (
    <aside className="space-y-1">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 pb-1">
        <FolderOpen className="h-4 w-4 text-indigo-500" />
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('blog.categories')}
        </h2>
      </div>

      {/* All articles link */}
      <Link
        href={`/blog/${locale}`}
        className={cn(
          'flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200',
          !activeCategorySlug
            ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md translate-x-1'
            : 'text-foreground/90 hover:translate-x-1 hover:bg-accent/70 hover:text-foreground'
        )}
      >
        <span>{t('blog.all_articles')}</span>
      </Link>

      {/* Category tree */}
      <nav className="space-y-1">
        {categories.map((category) => (
          <CategoryItem
            key={category.id}
            category={category}
            locale={locale}
            activeCategorySlug={activeCategorySlug ?? undefined}
            openCategoryId={openCategoryId}
            onToggle={handleToggle}
          />
        ))}
      </nav>

      {/* Tags section */}
      {tags.length > 0 && (
        <div className="mt-2 space-y-1 border-t border-border/70 pt-4">
          <div className="flex items-center justify-between gap-2 px-4 pb-1">
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-indigo-500" />
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t('blog.tags')}
              </h2>
            </div>
            {activeTags.length > 0 && (
              <Link
                href={`/blog/${locale}`}
                className="text-xs font-medium text-indigo-600 transition-colors hover:text-indigo-700 hover:underline dark:text-indigo-400 dark:hover:text-indigo-300"
              >
                {t('blog.clear_tags')}
              </Link>
            )}
          </div>
          <nav className="flex flex-wrap gap-2 px-4 pt-1">
            {tags.map((tag) => {
              const isActive = activeTags.includes(tag.slug)
              return (
                <Link
                  key={tag.slug}
                  href={getTagHref(tag.slug)}
                  className={cn(
                    'rounded-full px-2.5 py-1 text-xs font-medium transition-all duration-200',
                    isActive
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md'
                      : 'border border-border/70 bg-background/70 text-muted-foreground hover:border-indigo-400/50 hover:bg-accent/70 hover:text-foreground'
                  )}
                >
                  {tag.name}
                </Link>
              )
            })}
          </nav>
        </div>
      )}
    </aside>
  )
}
